import React from 'react';
import { OrganizationQuotaDetails, Organization, Plan } from '../../../types/domain';
import { formatBillingDate, getDaysRemaining, formatBytes, getPlanRank } from '../types';
import { OrganizationMeters } from './OrganizationMeters';
import { Button } from '../../../components/ui/Button';
import {
  AlertTriangle,
  RotateCcw,
  Sparkles,
  Crown,
  Calendar,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from 'lucide-react';

interface OrganizationBillingSectionProps {
  currentOrg: Organization | null;
  quota: OrganizationQuotaDetails | null;
  isLoadingQuota: boolean;
  plans: Plan[];
  isLoadingPlans: boolean;
  canManageOrg: boolean;
  isMutatingSubscription: boolean;
  onSelectUpgrade: (plan: Plan) => void;
  onSelectDowngrade: (plan: Plan) => void;
  onOpenCancelModal: () => void;
  onResumeSubscription: () => void;
  membersCount: number;
}

export const OrganizationBillingSection: React.FC<OrganizationBillingSectionProps> = ({
  currentOrg,
  quota,
  isLoadingQuota,
  plans,
  isLoadingPlans,
  canManageOrg,
  isMutatingSubscription,
  onSelectUpgrade,
  onSelectDowngrade,
  onOpenCancelModal,
  onResumeSubscription,
  membersCount,
}) => {
  const currentTierSlug = (quota?.plan_tier || currentOrg?.plan_tier || 'free').toLowerCase();

  return (
    <div className="space-y-6">
      {/* Pending Plan Change Warning Banner */}
      {(quota?.pending_plan_tier || quota?.cancel_at_period_end) && (
        <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                Plan Change Scheduled
              </h4>
              <p className="text-xs text-cf-textSecondary mt-0.5">
                A plan transition to{' '}
                <strong className="text-cf-textPrimary uppercase">
                  {quota.pending_plan_tier || 'FREE'}
                </strong>{' '}
                is scheduled for{' '}
                <strong className="text-cf-textPrimary">
                  {formatBillingDate(quota.current_period_end)}
                </strong>
                . Your current plan capabilities and limits will remain fully active until then.
              </p>
            </div>
          </div>
          {canManageOrg && (
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onResumeSubscription}
              disabled={isMutatingSubscription}
              loading={isMutatingSubscription}
              icon={<RotateCcw size={13} />}
              className="self-start sm:self-auto"
            >
              Keep Current Plan
            </Button>
          )}
        </div>
      )}

      {/* Current Subscription Hero & Cycle Dates */}
      <div className="p-5 rounded-lg bg-cf-surface border border-cf-border space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-cf-border pb-4">
          <div>
            <span className="text-xs text-cf-textMuted uppercase font-mono tracking-wider">
              Active Subscription Tier
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold text-cf-textPrimary flex items-center gap-1.5">
                {currentTierSlug === 'enterprise' || currentTierSlug === 'ultra' ? (
                  <Sparkles size={18} className="text-cf-orange" />
                ) : (
                  <Crown size={18} className="text-cf-orange" />
                )}
                <span className="uppercase">
                  {quota?.plan_tier || currentOrg?.plan_tier || 'FREE'} PLAN
                </span>
              </span>
              <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold uppercase">
                Active
              </span>
            </div>
          </div>

          <div className="flex items-center gap-6 text-xs text-cf-textSecondary">
            <div className="flex items-center gap-2">
              <Calendar size={15} className="text-cf-textMuted" />
              <div>
                <span className="text-cf-textMuted block text-xs">Period End / Renewal</span>
                <span className="font-semibold text-cf-textPrimary">
                  {formatBillingDate(quota?.current_period_end || currentOrg?.current_period_end)}
                </span>
              </div>
            </div>
            <div className="border-l border-cf-border pl-4">
              <span className="text-cf-textMuted block text-xs">Cycle Countdown</span>
              <span className="font-semibold text-cf-textPrimary">
                {getDaysRemaining(quota?.current_period_end || currentOrg?.current_period_end)} days left
              </span>
            </div>
          </div>
        </div>

        {/* 4 Quota Utilization Meters */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-cf-textPrimary flex items-center gap-1.5">
              <Layers size={14} className="text-cf-textMuted" />
              <span>Current Billing Cycle Resource Quotas</span>
            </h3>
            <span className="text-xs text-cf-textMuted">
              Submission counters reset automatically on every renewal date.
            </span>
          </div>

          {isLoadingQuota ? (
            <div className="py-8 flex items-center justify-center gap-2 text-xs text-cf-textSecondary">
              <Loader2 size={16} className="animate-spin text-cf-orange" />
              <span>Loading live quota metrics...</span>
            </div>
          ) : quota ? (
            <OrganizationMeters
              quota={quota}
              currentOrg={currentOrg}
              membersCount={membersCount}
              cardBgClass="bg-cf-bg"
            />
          ) : null}
        </div>
      </div>

      {/* Available Subscription Plans Grid */}
      <div className="p-5 rounded-lg bg-cf-surface border border-cf-border space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-cf-textPrimary">
              Available Subscription Plans
            </h3>
            <p className="text-xs text-cf-textSecondary mt-0.5">
              Upgrades take effect immediately with quota resets. Downgrades take effect at period end.
            </p>
          </div>
          {!canManageOrg && (
            <span className="text-xs text-amber-500 font-medium">
              Admin permissions required to manage plans
            </span>
          )}
        </div>

        {isLoadingPlans ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2">
            <Loader2 size={24} className="animate-spin text-cf-orange" />
            <span className="text-xs text-cf-textSecondary">Loading subscription plans...</span>
          </div>
        ) : plans.length === 0 ? (
          <div className="p-4 rounded-lg bg-cf-bg border border-cf-border text-center text-xs text-cf-textSecondary">
            No subscription plans currently configured.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
            {plans.map((plan) => {
              const isCurrent = currentTierSlug === plan.slug.toLowerCase();
              const isPending = (quota?.pending_plan_tier || '').toLowerCase() === plan.slug.toLowerCase();
              const targetRank = getPlanRank(plan, plans);
              const currentRank = getPlanRank(currentTierSlug, plans);
              const isUpgrade = targetRank > currentRank;
              const isDowngrade = targetRank < currentRank;
              const priceStr =
                plan.price_monthly_cents === 0
                  ? '$0 / mo'
                  : `$${(plan.price_monthly_cents / 100).toFixed(0)} / mo`;

              return (
                <div
                  key={plan.id}
                  className={`p-4 rounded-lg flex flex-col justify-between relative overflow-hidden transition-all ${
                    isCurrent
                      ? 'bg-cf-surfaceActive border border-cf-orange/50 shadow-xs'
                      : isPending
                      ? 'bg-cf-surface border border-amber-500/50 shadow-xs'
                      : 'bg-cf-bg border border-cf-border hover:border-cf-borderActive'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute top-0 right-0 bg-cf-orange text-cf-bg text-xs font-bold px-2 py-0.5 rounded-bl uppercase tracking-wider">
                      Active Plan
                    </div>
                  )}
                  {isPending && (
                    <div className="absolute top-0 right-0 bg-amber-500 text-cf-bg text-xs font-bold px-2 py-0.5 rounded-bl uppercase tracking-wider">
                      Scheduled
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-cf-textPrimary flex items-center gap-1">
                        {plan.slug === 'enterprise' || plan.slug === 'ultra' ? (
                          <Sparkles size={13} className="text-cf-orange" />
                        ) : plan.slug === 'pro' || plan.slug === 'max' ? (
                          <Crown size={13} className="text-cf-orange" />
                        ) : null}
                        <span>{plan.name}</span>
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          isCurrent || plan.slug === 'pro' ? 'text-cf-orange' : 'text-cf-textSecondary'
                        }`}
                      >
                        {priceStr}
                      </span>
                    </div>

                    {plan.description && (
                      <p className="text-xs text-cf-textMuted mb-2.5 line-clamp-2">
                        {plan.description}
                      </p>
                    )}

                    <ul className="text-xs text-cf-textSecondary space-y-1.5 mb-4">
                      <li>• {plan.max_monthly_submissions.toLocaleString()} monthly submissions</li>
                      <li>• {plan.max_tables.toLocaleString()} datastore tables</li>
                      <li>• {plan.max_team_seats.toLocaleString()} team seats</li>
                      <li>• {plan.retention_days} days retention</li>
                      {plan.max_storage_bytes && (
                        <li>• {formatBytes(plan.max_storage_bytes)} attachment storage</li>
                      )}
                      {Array.isArray(plan.features) &&
                        plan.features.map((feat, idx) => <li key={idx}>• {feat}</li>)}
                    </ul>
                  </div>

                  {/* Card Action Button */}
                  <div className="pt-3 border-t border-cf-border/60">
                    {isCurrent ? (
                      currentTierSlug !== 'free' && !quota?.cancel_at_period_end ? (
                        <Button
                          type="button"
                          variant="danger"
                          size="xs"
                          fullWidth
                          onClick={onOpenCancelModal}
                          disabled={!canManageOrg || isMutatingSubscription}
                        >
                          Cancel Subscription
                        </Button>
                      ) : (
                        <span className="block text-center py-1.5 text-xs text-cf-textMuted font-medium bg-cf-surface border border-cf-border/50 rounded">
                          Current Active Plan
                        </span>
                      )
                    ) : isPending ? (
                      <span className="block text-center py-1.5 text-xs text-amber-500 font-medium bg-amber-500/10 border border-amber-500/20 rounded">
                        Scheduled at Period End
                      </span>
                    ) : isUpgrade ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="xs"
                        fullWidth
                        onClick={() => onSelectUpgrade(plan)}
                        disabled={!canManageOrg || isMutatingSubscription}
                        icon={<ArrowUpRight size={13} />}
                      >
                        Upgrade to {plan.name}
                      </Button>
                    ) : isDowngrade ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="xs"
                        fullWidth
                        onClick={() => onSelectDowngrade(plan)}
                        disabled={!canManageOrg || isMutatingSubscription}
                        icon={<ArrowDownRight size={13} />}
                      >
                        Downgrade to {plan.name}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
